import { Link, useNavigate } from "react-router-dom";
import { useEffect } from "react";
import { useAuth, homeFor } from "@/lib/auth";
import Logo from "@/components/Logo";
import { ArrowRight, ShieldCheck, MapPin, Car } from "lucide-react";

export default function Splash() {
  const { user, loading } = useAuth();
  const nav = useNavigate();

  useEffect(() => {
    if (!loading && user) nav(homeFor(user.role), { replace: true });
  }, [user, loading, nav]);

  return (
    <div className="min-h-screen bg-white flex items-center justify-center p-6 relative overflow-hidden">
      {/* Decorative grid */}
      <div
        className="absolute inset-0 opacity-[0.04] pointer-events-none"
        style={{
          backgroundImage:
            "linear-gradient(#0f172a 1px, transparent 1px), linear-gradient(90deg, #0f172a 1px, transparent 1px)",
          backgroundSize: "44px 44px",
        }}
      />
      {/* Cyan glow */}
      <div className="absolute -top-32 -right-32 w-[420px] h-[420px] bg-primary/20 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute -bottom-32 -left-32 w-[420px] h-[420px] bg-primary/10 rounded-full blur-3xl pointer-events-none" />

      <div className="relative z-10 max-w-md w-full mx-auto fade-up" data-testid="splash-content">
        <div className="flex justify-center mb-6">
          <Logo size={120} />
        </div>

        <p className="text-center text-slate-500 font-medium tracking-widest text-xs uppercase mb-2">
          ENEC · Emirates Nuclear Energy
        </p>
        <h1 className="font-display text-4xl sm:text-5xl font-bold text-center text-slate-900 tracking-tight">
          Be Safe.<br />
          <span className="text-primary">Travel Smart.</span>
        </h1>
        <p className="text-center text-slate-500 mt-4 text-base max-w-sm mx-auto">
          The single transport platform for every ENEC employee, driver and dispatcher. From Abu Dhabi to Barakah, in a tap.
        </p>

        <div className="mt-10 grid grid-cols-3 gap-3">
          {[
            { icon: Car, label: "Book a ride", testId: "feature-book" },
            { icon: MapPin, label: "Live tracking", testId: "feature-track" },
            { icon: ShieldCheck, label: "ENEC secure", testId: "feature-secure" },
          ].map((f) => (
            <div
              key={f.label}
              data-testid={f.testId}
              className="bg-white border border-slate-200/70 rounded-2xl p-4 flex flex-col items-center gap-2 shadow-sm"
            >
              <f.icon size={20} className="text-primary" strokeWidth={2.2} />
              <span className="text-[11px] font-semibold text-slate-600 text-center leading-tight">
                {f.label}
              </span>
            </div>
          ))}
        </div>

        <div className="mt-10 space-y-3">
          <Link
            to="/login"
            data-testid="splash-employee-cta"
            className="w-full h-14 rounded-2xl bg-primary hover:bg-primary-600 text-white font-semibold text-lg flex items-center justify-center gap-2 transition-colors"
          >
            I&apos;m an Employee
            <ArrowRight size={18} />
          </Link>
          <Link
            to="/login/driver"
            data-testid="splash-driver-cta"
            className="w-full h-12 rounded-xl bg-slate-900 hover:bg-slate-800 text-white font-semibold flex items-center justify-center transition-colors text-sm"
          >
            Driver Login
          </Link>
        </div>

        <p className="text-center text-xs text-slate-400 mt-10 font-mono">
          v1.0.0 · Abu Dhabi · تسلم
        </p>
      </div>
    </div>
  );
}
